import React, { createContext, useRef, useCallback } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { useSecurity } from '@/store';
import useExchange from '@/store/exchange/useExchange';

const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY;

export const RecaptchaContext = createContext(null);

export function RecaptchaProvider({ children }) {
  const { skipRToken } = useExchange();
  const { generateSecurityToken } = useSecurity();

  const { t } = useTranslation();

  const recaptchaRef = useRef(null);

  const execute = useCallback(async () => {
    if (!recaptchaRef.current) {
      throw new Error('reCAPTCHA not initialized');
    }

    const token = await recaptchaRef.current.executeAsync();
    recaptchaRef.current.reset();
    return token;
  }, []);

  const reset = useCallback(() => {
    recaptchaRef.current?.reset();
  }, []);

  const handleRecaptcha = async () => {
    if (skipRToken) {
      return {
        token: true,
        recaptchaToken: process.env.NEXT_PUBLIC_RECAPTCHA_SKIP_SECRET,
      };
    }

    let recaptchaToken = null;

    try {
      recaptchaToken = await execute();
    } catch (error) {
      return alert(t('checkout.home.alerts.recaptchaError'));
    }

    if (!recaptchaToken) {
      return alert(t('checkout.home.alerts.recaptchaError'));
    }

    const token = await generateSecurityToken();

    return { token, recaptchaToken };
  };

  return (
    <RecaptchaContext.Provider value={{ execute, reset, handleRecaptcha }}>
      {children}
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={RECAPTCHA_SITE_KEY}
        size="invisible"
        badge="bottomright"
      />
    </RecaptchaContext.Provider>
  );
}
